import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#D2D2D7] rounded-2xl px-5 py-3.5 text-sm text-[#1D1D1F] placeholder:text-[#86868B] focus:outline-none focus:border-[#2C3E35] focus:ring-2 focus:ring-[#2C3E35]/10 transition-[border-color,box-shadow] duration-200";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", inquiry_type: "partnership", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name || !email || !message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, name, email, message });
      toast.success("Inquiry received. Our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", inquiry_type: "partnership", message: "", website: "" });
    } catch (err) {
      if (err.response?.status === 429) {
        const msg =
          err.response.data?.detail ||
          "You've sent a few inquiries very recently. Please try again in a few minutes.";
        toast.error(msg, { duration: 6000 });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 lg:py-40 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 max-w-xl mx-auto"
        >
          <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">Partner With Us</p>
          <h1 data-testid="contact-heading" className="font-display text-4xl md:text-5xl leading-tight tracking-tight font-medium text-[#1D1D1F]">
            Bring pure water to your table.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
            From luxury hospitality to premium retail — join the establishments serving the
            world's purest, most thoughtfully crafted hydration.
          </p>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="space-y-4 max-w-xl mx-auto"
        >
          {/* Honeypot — invisible to real users, filled only by scrapers. Kept out of the tab
              order and hidden from assistive tech. If a value arrives, the backend silently
              drops the submission without consuming rate-limit budget. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-10000px",
              top: "auto",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label htmlFor="nyla-website">Website (leave blank)</label>
            <input
              id="nyla-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={set("website")}
              data-testid="contact-honeypot"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-input-name" className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} />
            <input data-testid="contact-input-email" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
          </div>
          <input data-testid="contact-input-company" className={inputCls} placeholder="Company / Establishment" value={form.company} onChange={set("company")} />
          <select
            data-testid="contact-select-type"
            className={`${inputCls} appearance-none cursor-pointer bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2386868B%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_1.25rem_center]`}
            value={form.inquiry_type}
            onChange={set("inquiry_type")}
          >
            <option value="partnership">B2B Partnership</option>
            <option value="hospitality">Luxury Hospitality</option>
            <option value="retail">Premium Retail</option>
            <option value="press">Press & Media</option>
            <option value="other">Other</option>
          </select>
          <textarea
            data-testid="contact-input-message"
            rows={5}
            className={`${inputCls} resize-none`}
            placeholder="Tell us about your inquiry *"
            value={form.message}
            onChange={set("message")}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#1D1D1F] text-white text-sm font-medium hover:bg-black active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-60"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            {sending ? "Sending..." : "Send Inquiry"}
          </button>
        </motion.form>

        <motion.div
          data-testid="contact-direct"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 md:mt-20 rounded-3xl bg-[#F5F5F7] px-6 py-10 md:px-12 md:py-12"
        >
          <p className="text-center text-[11px] tracking-[0.25em] uppercase font-semibold text-[#86868B] mb-6 md:mb-8">
            Reach us directly
          </p>
          <div className="max-w-lg mx-auto divide-y divide-[#D2D2D7]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-4">
              <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B]">General</p>
              <a
                href="mailto:contactus@nylaairwater.earth"
                data-testid="direct-email-general"
                className="text-sm text-[#1D1D1F] hover:text-[#2C3E35] transition-colors duration-200"
              >
                contactus@nylaairwater.earth
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-4">
              <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B]">Sales</p>
              <a
                href="mailto:sales@nylaairwater.earth"
                data-testid="direct-email-sales"
                className="text-sm text-[#1D1D1F] hover:text-[#2C3E35] transition-colors duration-200"
              >
                sales@nylaairwater.earth
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-4">
              <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B]">Support</p>
              <a
                href="mailto:support@nylaairwater.earth"
                data-testid="direct-email-support"
                className="text-sm text-[#1D1D1F] hover:text-[#2C3E35] transition-colors duration-200"
              >
                support@nylaairwater.earth
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-4">
              <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B]">Response time</p>
              <p data-testid="direct-response-time" className="text-sm text-[#1D1D1F]">
                Within 24 hours
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          data-testid="contact-info"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6"
        >
          <div className="bg-[#F5F5F7] rounded-3xl p-6">
            <MapPin className="w-5 h-5 text-[#2C3E35] mb-3" strokeWidth={1.6} />
            <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B] mb-1.5">Corporate Office</p>
            <p className="text-sm text-[#1D1D1F] leading-relaxed">
              Plot No. 78, 3rd Floor,<br />
              CBI Colony, Kavuri Hills,<br />
              Madhapur, Hyderabad — 500033, India
            </p>
          </div>
          <div className="bg-[#F5F5F7] rounded-3xl p-6">
            <Mail className="w-5 h-5 text-[#2C3E35] mb-3" strokeWidth={1.6} />
            <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B] mb-1.5">Write to Us</p>
            <a
              href="mailto:contactus@nylaairwater.earth"
              data-testid="contact-email-link"
              className="text-sm text-[#1D1D1F] leading-relaxed hover:text-[#2C3E35] transition-colors duration-200 break-all"
            >
              contactus@nylaairwater.earth
            </a>
            <p className="mt-3 text-xs text-[#86868B]">www.nylaairwater.earth</p>
          </div>
          <div className="bg-[#F5F5F7] rounded-3xl p-6">
            <Phone className="w-5 h-5 text-[#2C3E35] mb-3" strokeWidth={1.6} />
            <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B] mb-1.5">Call Us</p>
            <a
              href="tel:+919297979497"
              data-testid="contact-phone-link"
              className="text-sm text-[#1D1D1F] leading-relaxed hover:text-[#2C3E35] transition-colors duration-200"
            >
              +91 92979 79497
            </a>
            <p className="mt-3 text-xs text-[#86868B]">@nylaairwater on social</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-12 text-center"
        >
          <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#86868B] mb-3">
            Now Serving
          </p>
          <p data-testid="contact-cities" className="text-sm md:text-base text-[#1D1D1F]">
            Hyderabad · Bengaluru · Mumbai · Goa · Delhi
          </p>
        </motion.div>
      </div>
    </section>
  );
};
